import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Building2, Plus, Phone, Mail, Globe, MapPin, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';

const organismes = [
  { 
    name: "CAF", 
    fullName: "Caisse d'Allocations Familiales",
    description: "APL, RSA, Prime d'activité",
    site: "caf.fr",
    color: "#7EC8C8"
  },
  { 
    name: "Impôts", 
    fullName: "Direction Générale des Finances Publiques",
    description: "Impôt sur le revenu, taxe d'habitation",
    site: "impots.gouv.fr",
    color: "#4ECDC4"
  },
  { 
    name: "Ameli", 
    fullName: "Assurance Maladie",
    description: "Carte vitale, remboursements",
    site: "ameli.fr",
    color: "#FF6B6B"
  },
  { 
    name: "CROUS", 
    fullName: "Centre Régional des Œuvres Universitaires",
    description: "Bourses, logement étudiant",
    site: "messervices.etudiant.gouv.fr",
    color: "#7EC8C8"
  },
];

export const OrganismePage = () => {
  return (
    <div className="min-h-screen bg-[#1A1A2E] px-4 py-6" data-testid="organisme-page">
      <div className="max-w-4xl mx-auto">
        <Link 
          to="/dashboard" 
          className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-[#7EC8C8] mb-6 transition-colors"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft size={18} />
          <span>Retour au dashboard</span>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-2xl bg-[#FF6B6B]/20 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#FF6B6B]" />
            </div>
            <h1 
              className="text-3xl font-bold text-[#F8F9FA]"
              style={{ fontFamily: 'Outfit' }}
            >
              Organismes
            </h1>
          </div>
          <p className="text-[#94A3B8] mb-8">
            Tous les contacts et infos des organismes administratifs au même endroit.
          </p>
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <Input
            placeholder="Rechercher un organisme..."
            className="bg-[#F8F9FA]/5 border-[#7EC8C8]/20 text-[#F8F9FA] placeholder:text-[#94A3B8]/50"
            data-testid="search-organisme"
          />
        </motion.div>

        {/* Organismes list */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="text-lg font-semibold text-[#F8F9FA] mb-4" style={{ fontFamily: 'Outfit' }}>
            Organismes principaux
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {organismes.map((org, index) => (
              <Card 
                key={org.name}
                className="bg-[#1A1A2E]/50 border-[#7EC8C8]/20 p-5 hover:border-[#7EC8C8]/40 transition-all cursor-pointer group"
              >
                <div className="flex items-start gap-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold"
                    style={{ backgroundColor: `${org.color}20`, color: org.color }}
                  >
                    {org.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-[#F8F9FA] font-semibold">{org.name}</h4>
                    <p className="text-xs text-[#94A3B8] mb-1 truncate">{org.fullName}</p>
                    <p className="text-sm text-[#94A3B8]">{org.description}</p>
                    <a 
                      href={`https://${org.site}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs mt-2 hover:text-[#7EC8C8] transition-colors"
                      style={{ color: org.color }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Globe className="w-3 h-3" />
                      {org.site}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </motion.div>

        {/* Add custom */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8"
        >
          <Card className="bg-[#1A1A2E]/50 border-[#7EC8C8]/20 border-dashed p-6 text-center">
            <Button
              variant="ghost"
              className="text-[#7EC8C8] hover:text-[#4ECDC4] hover:bg-[#7EC8C8]/10"
              data-testid="add-organisme"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter un organisme personnalisé
            </Button>
            <p className="text-sm text-[#94A3B8] mt-2">
              Mutuelle, employeur, fournisseur d'énergie...
            </p>
          </Card>
        </motion.div>

        {/* Contact info template */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8"
        >
          <h2 className="text-lg font-semibold text-[#F8F9FA] mb-4" style={{ fontFamily: 'Outfit' }}>
            Informations utiles
          </h2>
          <Card className="bg-[#7EC8C8]/5 border-[#7EC8C8]/20 p-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div>
                <Phone className="w-5 h-5 text-[#7EC8C8] mx-auto mb-2" />
                <p className="text-[#F8F9FA] font-medium">3230</p>
                <p className="text-xs text-[#94A3B8]">Service public (gratuit)</p>
              </div>
              <div>
                <Mail className="w-5 h-5 text-[#4ECDC4] mx-auto mb-2" />
                <p className="text-[#F8F9FA] font-medium">service-public.fr</p>
                <p className="text-xs text-[#94A3B8]">Questions admin</p>
              </div>
              <div>
                <MapPin className="w-5 h-5 text-[#FF6B6B] mx-auto mb-2" />
                <p className="text-[#F8F9FA] font-medium">France Services</p>
                <p className="text-xs text-[#94A3B8]">Point d'accueil près de toi</p>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default OrganismePage;
